import type {
  CreateTrackerTemplateDto,
  TrackerHue,
  TrackerIcon,
  TrackerItemSource,
  TrackerLayoutDto,
  TrackerTemplateResponseDto,
  UpdateTrackerTemplateDto,
} from '@inference.engine/workspace-api-client/web';
import { i18n } from '@/shared/i18n';

export type {
  CreateTrackerTemplateDto,
  UpdateTrackerTemplateDto,
  TrackerTemplateResponseDto as TrackerTemplate,
};

/** Who can see + reuse a saved template. */
export type TrackerTemplateVisibility = 'private' | 'workspace' | 'organization';

/**
 * Visibility options in creator-facing order, with labels.
 *
 * Labels and descriptions are getters so each read resolves against the active
 * locale; a plain `i18n.t(...)` here would freeze the English at import time.
 */
export const TEMPLATE_VISIBILITIES: ReadonlyArray<{
  value: TrackerTemplateVisibility;
  label: string;
  description: string;
}> = [
  {
    value: 'private',
    get label() {
      return i18n.t('trackers:trackerTemplate.types.private');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.types.onlyYouCanSeeAnd');
    },
  },
  {
    value: 'workspace',
    get label() {
      return i18n.t('trackers:trackerTemplate.types.workspace');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.types.everyoneInThisWorkspace');
    },
  },
  {
    value: 'organization',
    get label() {
      return i18n.t('trackers:trackerTemplate.types.organization');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.types.everyoneInYourOrganization');
    },
  },
];

/**
 * A product/default template shipped in code (not stored in the DB). Its
 * `layout` is a `TrackerLayoutDto` snapshot applied to a fresh board.
 */
export interface BuiltinTrackerTemplate {
  key: string;
  /** What the gallery shows and searches, and the new tracker's own name. */
  displayName: string;
  description: string;
  icon: TrackerIcon;
  hue: TrackerHue;
  itemSource: TrackerItemSource;
  groupBy?: string;
  layout: TrackerLayoutDto;
}

/**
 * Normalized model the gallery renders — either a built-in (product) template
 * or a user-saved one. `source` drives the "Default vs Saved" grouping + badges.
 */
export type TemplateCard =
  | { source: 'builtin'; id: string; builtin: BuiltinTrackerTemplate }
  | { source: 'user'; id: string; template: TrackerTemplateResponseDto };
